import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bus, GraduationCap, Accessibility, HeartPulse, FileSignature, ShieldCheck,
  Wrench, ClipboardList, MessageSquare, Handshake, Users, CalendarCheck,
  ArrowRight, ChevronLeft, ChevronRight, X, Star, Loader2, Camera, CheckCircle2, AlertCircle, MapPin,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_CONTENT = {
  areas: ["Wigan", "Bolton", "Bury", "Salford", "Greater Manchester", "North West England"],
  desc_home_school: "We provide contracted home-to-school minibus transport, supporting local authorities and educational establishments with scheduled passenger services. Our focus is on reliable route delivery, safeguarding, operational planning and consistent communication.",
  desc_send: "We welcome enquiries for transport arrangements supporting children and young people with special educational needs and disabilities.",
  desc_nept: "We welcome enquiries from healthcare organisations and commissioners regarding planned non-emergency patient transport.",
  desc_contracted: "We welcome enquiries for regular contracted passenger transport, from local authority frameworks to long-term minibus service agreements.",
};

const TRANSPORT_TYPES = [
  "Home-to-school",
  "SEND transport",
  "Non-emergency patient transport",
  "Local authority contract",
  "NHS transport contract",
  "Minibus subcontracting",
  "Other",
];

const inputCls =
  "w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3.5 text-sm text-[#0B192C] placeholder:text-[#94A3B8] outline-none transition-colors duration-300 focus:border-[#10B981] focus:bg-white";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#475569] mb-2";

const Transport = () => {
  const [content, setContent] = useState(FALLBACK_CONTENT);
  const [fleet, setFleet] = useState(null);
  const [lightbox, setLightbox] = useState(-1);
  const servicesRef = useScrollRef();
  const formRef = useScrollRef();

  useEffect(() => {
    axios.get(`${API}/transport/content`).then((r) => setContent({ ...FALLBACK_CONTENT, ...r.data })).catch(() => {});
    axios.get(`${API}/fleet`).then((r) => setFleet(r.data)).catch(() => setFleet([]));
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox < 0) return;
      if (e.key === "Escape") setLightbox(-1);
      if (e.key === "ArrowRight" && fleet) setLightbox((i) => Math.min(i + 1, fleet.length - 1));
      if (e.key === "ArrowLeft" && fleet) setLightbox((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, fleet]);

  const featured = fleet?.find((p) => p.featured) || fleet?.[0] || null;

  const SERVICES = [
    {
      id: "home-school",
      icon: GraduationCap,
      color: "#10B981",
      title: "Home-to-School Transport",
      desc: content.desc_home_school,
      bullets: ["Daily home-to-school transport", "Local authority school contracts", "Scheduled minibus routes", "School and college transport", "Route coordination", "Dedicated driver arrangements", "Contracted passenger transport"],
    },
    {
      id: "send",
      icon: Accessibility,
      color: "#06B6D4",
      title: "SEND & Specialist Transport",
      desc: content.desc_send,
      bullets: ["SEND school transport", "Individual and small-group routes", "Passenger assistant arrangements", "Planned and consistent transport", "Accessibility requirements", "Individual transport arrangements"],
      note: "Any specialist provision is subject to suitable vehicles, appropriately trained staff and contract requirements.",
    },
    {
      id: "nept",
      icon: HeartPulse,
      color: "#F59E0B",
      title: "Non-Emergency Patient Transport",
      desc: content.desc_nept,
      bullets: ["Hospital appointment journeys", "Outpatient transport", "Planned healthcare journeys", "Community healthcare transport", "Dialysis appointment transport", "Contracted patient transport programmes"],
      note: "Availability depends on service capability, applicable licensing, insurance and regulatory requirements. NCS Transport does not provide emergency ambulance services, medical monitoring or specialist clinical transport.",
    },
    {
      id: "contracted",
      icon: FileSignature,
      color: "#EF4444",
      title: "Contracted Minibus & Passenger Transport",
      desc: content.desc_contracted,
      bullets: ["Local authority contracts", "NHS and healthcare transport contracts", "Education transport", "Community transport arrangements", "Subcontracting opportunities", "Long-term minibus service agreements"],
    },
  ];

  const WHY = [
    { icon: CalendarCheck, color: "#10B981", title: "Reliable contracted transport" },
    { icon: ShieldCheck, color: "#06B6D4", title: "Safeguarding-focused service delivery" },
    { icon: Wrench, color: "#F59E0B", title: "Planned vehicle maintenance" },
    { icon: ClipboardList, color: "#EF4444", title: "Professional operational coordination" },
    { icon: MessageSquare, color: "#10B981", title: "Clear communication with commissioners" },
    { icon: Handshake, color: "#06B6D4", title: "Flexible contract discussions" },
    { icon: HeartPulse, color: "#F59E0B", title: "Commitment to passenger safety" },
    { icon: Users, color: "#EF4444", title: "Long-term working relationships" },
  ];

  return (
    <main data-testid="transport-page">
      <Seo
        title="Transport Services — Passenger Transport Solutions | NCS"
        description="Reliable, professionally managed passenger transport for local authorities, schools and healthcare organisations across Greater Manchester and the North West."
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-16 sm:pb-20">
        <div className="absolute inset-0 dots-bg pointer-events-none [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#B45309]"
            >
              NCS Transport&nbsp;&nbsp;·&nbsp;&nbsp;Passenger Transport Solutions
            </motion.p>
            <h1
              data-testid="transport-heading"
              className="mt-5 font-display font-extrabold tracking-tight leading-[1.03] text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl"
            >
              <MaskedLine delay={0.25}>Professional Passenger</MaskedLine>
              <MaskedLine delay={0.4}><span className="font-serif-accent">Transport Solutions</span></MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65 }}
              className="mt-6 text-base sm:text-lg leading-relaxed text-[#475569] max-w-2xl"
            >
              Providing reliable, professionally managed passenger transport solutions for local
              authorities, schools, educational establishments and healthcare organisations. We
              specialise in contracted home-to-school transport and welcome enquiries regarding
              SEND transport, healthcare transport and other passenger transport requirements.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <button
                data-testid="transport-explore-btn"
                onClick={() => servicesRef.current?.scrollIntoView({ behavior: "smooth" })}
                className="group btn-shine inline-flex items-center gap-2.5 bg-[#F59E0B] text-[#0B192C] font-bold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0B192C] hover:text-white"
              >
                Explore Our Transport Services
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                data-testid="transport-quote-btn"
                onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2.5 bg-white text-[#0B192C] font-bold px-7 py-4 rounded-full text-sm border border-[#E2E8F0] transition-colors duration-300 hover:border-[#0B192C]"
              >
                Request a Transport Quote
              </button>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, rotate: 2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <img src="/assets/ncs-mark.png?v=2" alt="" aria-hidden="true"
                className="absolute -top-12 -left-12 w-36 opacity-20 -rotate-12 select-none pointer-events-none" />
              <div className="relative rounded-[2rem] overflow-hidden border-[6px] border-white shadow-[0_16px_40px_-12px_rgba(11,25,44,0.12)]">
                {featured ? (
                  <img
                    src={`${API}/fleet/image/${featured.id}`}
                    alt={featured.alt || featured.title}
                    data-testid="transport-hero-image"
                    className="w-full h-[340px] sm:h-[440px] object-cover"
                  />
                ) : (
                  <div data-testid="transport-hero-placeholder" className="w-full h-[340px] sm:h-[440px] bg-[#0B192C] flex flex-col items-center justify-center gap-4 text-center px-8">
                    <Bus size={44} className="text-[#10B981]" />
                    <p className="font-display text-xl font-bold text-white">Our fleet photographs are on their way</p>
                    <p className="text-sm text-white/55 max-w-xs">Vehicle images are uploaded by the NCS team through the administration dashboard.</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section ref={servicesRef} className="py-20 sm:py-28 lg:py-32 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Our transport services</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
              Contracted Passenger Transport, Planned Properly
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={(i % 2) * 0.08}>
                <div
                  data-testid={`transport-service-${s.id}`}
                  className="spotlight-card h-full bg-white border border-[#E2E8F0] rounded-3xl p-8 sm:p-10 transition-shadow duration-500 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]"
                  onMouseMove={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                  }}
                >
                  <span
                    className="w-13 h-13 rounded-2xl flex items-center justify-center p-3"
                    style={{ backgroundColor: `${s.color}1A`, color: s.color }}
                  >
                    <s.icon size={26} />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-bold text-[#0B192C]">{s.title}</h3>
                  <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#475569]">{s.desc}</p>
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm text-[#0B192C]">
                        <CheckCircle2 size={15} className="shrink-0 mt-0.5" style={{ color: s.color }} />
                        {b}
                      </li>
                    ))}
                  </ul>
                  {s.note && (
                    <p className="mt-6 flex items-start gap-2.5 text-xs leading-relaxed text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3">
                      <AlertCircle size={14} className="shrink-0 mt-0.5 text-[#F59E0B]" />
                      {s.note}
                    </p>
                  )}
                  <button
                    data-testid={`transport-service-cta-${s.id}`}
                    onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth" })}
                    className="group mt-7 inline-flex items-center gap-2 text-sm font-bold text-[#0B192C] border-b-2 pb-1 transition-colors hover:text-[#10B981]"
                    style={{ borderColor: s.color }}
                  >
                    Discuss Your Transport Requirements
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FLEET GALLERY */}
      <section className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Our fleet</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C]">
              Explore the Vehicles Supporting Our Passenger Transport Operations
            </h2>
          </Reveal>

          {!fleet ? (
            <Loader2 size={26} className="animate-spin text-[#10B981] mx-auto mt-16" />
          ) : fleet.length === 0 ? (
            <Reveal>
              <div data-testid="fleet-empty" className="mt-12 max-w-2xl mx-auto border-2 border-dashed border-[#E2E8F0] rounded-[2rem] p-12 text-center bg-[#F8FAFC]">
                <Camera size={36} className="mx-auto text-[#94A3B8]" />
                <p className="mt-5 font-display text-xl font-bold text-[#0B192C]">Fleet photographs coming soon</p>
                <p className="mt-2 text-sm text-[#64748B]">
                  Our vehicle gallery is updated by the NCS team — please check back shortly.
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="fleet-gallery">
              {fleet.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.08}>
                  <button
                    data-testid={`fleet-photo-${p.id}`}
                    onClick={() => setLightbox(i)}
                    className="group relative block w-full rounded-3xl overflow-hidden border border-[#E2E8F0] bg-[#0B192C] text-left transition-shadow duration-500 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]"
                  >
                    <img
                      src={`${API}/fleet/image/${p.id}`}
                      alt={p.alt || p.title}
                      loading="lazy"
                      className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between gap-3">
                      <div className="bg-white/95 backdrop-blur rounded-xl px-4 py-2.5">
                        <p className="text-sm font-bold text-[#0B192C] flex items-center gap-2">
                          {p.featured && <Star size={13} className="fill-[#F59E0B] text-[#F59E0B]" />}
                          {p.title}
                        </p>
                        {p.description && <p className="text-xs text-[#64748B] mt-0.5 line-clamp-1">{p.description}</p>}
                      </div>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
          <Reveal className="mt-8">
            <p className="text-xs text-[#94A3B8] text-center max-w-xl mx-auto leading-relaxed">
              We never publish vehicle photographs containing identifiable children or passengers
              without appropriate permission.
            </p>
          </Reveal>
        </div>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightbox >= 0 && fleet && fleet[lightbox] && (
          <motion.div
            data-testid="fleet-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-[#0B192C]/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
            onClick={() => setLightbox(-1)}
          >
            <button aria-label="Close" data-testid="lightbox-close"
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/25 transition-colors"
              onClick={() => setLightbox(-1)}>
              <X size={20} />
            </button>
            {lightbox > 0 && (
              <button aria-label="Previous photo" data-testid="lightbox-prev"
                className="absolute left-3 sm:left-6 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/25 transition-colors"
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => Math.max(i - 1, 0)); }}>
                <ChevronLeft size={20} />
              </button>
            )}
            {lightbox < fleet.length - 1 && (
              <button aria-label="Next photo" data-testid="lightbox-next"
                className="absolute right-3 sm:right-6 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/25 transition-colors"
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => Math.min(i + 1, fleet.length - 1)); }}>
                <ChevronRight size={20} />
              </button>
            )}
            <motion.figure
              key={fleet[lightbox].id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={`${API}/fleet/image/${fleet[lightbox].id}`}
                alt={fleet[lightbox].alt || fleet[lightbox].title}
                className="w-full max-h-[75vh] object-contain rounded-2xl"
              />
              <figcaption className="mt-4 text-center">
                <p className="font-display text-lg font-bold text-white">{fleet[lightbox].title}</p>
                {fleet[lightbox].description && (
                  <p className="text-sm text-white/60 mt-1">{fleet[lightbox].description}</p>
                )}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WHY PARTNER */}
      <section className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#F59E0B]">Why partner with NCS Transport?</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
              A Transport Partner Built on Reliability and Safeguarding
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={(i % 4) * 0.07}>
                <div className="h-full bg-white border border-[#E2E8F0] rounded-3xl p-7 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
                  <span
                    className="w-11 h-11 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${w.color}1A`, color: w.color }}
                  >
                    <w.icon size={20} />
                  </span>
                  <h3 className="mt-5 font-display text-base font-bold text-[#0B192C] leading-snug">{w.title}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OPERATING AREAS */}
      <section className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Our operating areas</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl mx-auto">
              Supporting Transport Requirements Across Greater Manchester & the North West
            </h2>
            <div className="mt-10 flex flex-wrap justify-center gap-3" data-testid="transport-areas">
              {(content.areas || []).map((a) => (
                <span
                  key={a}
                  className="inline-flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full px-5 py-2.5 text-sm font-bold text-[#0B192C]"
                >
                  <MapPin size={14} className="text-[#EF4444]" />
                  {a}
                </span>
              ))}
            </div>
            <p className="mt-7 text-xs text-[#64748B] max-w-xl mx-auto leading-relaxed">
              Contract availability and geographical coverage are subject to operational capacity —
              please enquire to discuss your area.
            </p>
          </Reveal>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section ref={formRef} className="py-20 sm:py-28 lg:py-32 scroll-mt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Request a transport quote</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C]">
              Discuss Your Transport Requirements
            </h2>
            <p className="mt-4 text-base text-[#475569] max-w-xl mx-auto">
              Two minutes to complete — our transport team will respond with availability and a
              tailored quotation.
            </p>
          </Reveal>
          <Reveal>
            <TransportQuoteForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
};

function useScrollRef() {
  const [ref] = useState(() => ({ current: null }));
  return ref;
}

const TransportQuoteForm = () => {
  const [form, setForm] = useState({
    full_name: "", organisation: "", email: "", phone: "",
    transport_type: "Home-to-school", passengers: "", pickup_area: "",
    destination_area: "", days_of_operation: "", start_date: "",
    contract_duration: "", additional: "", website: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        full_name: form.full_name,
        organisation: form.organisation,
        org_type: "Other",
        email: form.email,
        phone: form.phone,
        requirement: `Transport — ${form.transport_type}`,
        start_date: form.start_date || null,
        additional: form.additional || null,
        journey: "transport",
        website: form.website,
        calculator: [
          { label: "Transport type", value: form.transport_type },
          { label: "Passengers", value: form.passengers || "—" },
          { label: "Pickup area", value: form.pickup_area || "—" },
          { label: "Destination area", value: form.destination_area || "—" },
          { label: "Days of operation", value: form.days_of_operation || "—" },
          { label: "Contract duration", value: form.contract_duration || "—" },
        ],
      });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Something went wrong. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="tq-success"
        className="bg-white border border-[#10B981]/30 rounded-[2rem] p-10 sm:p-14 text-center shadow-[0_10px_30px_rgba(11,25,44,0.04)]"
      >
        <span className="mx-auto w-16 h-16 rounded-full bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
          <CheckCircle2 size={30} />
        </span>
        <h3 className="mt-6 font-display text-2xl font-bold text-[#0B192C]">Transport enquiry received</h3>
        <p className="mt-3 text-sm sm:text-base text-[#475569] max-w-md mx-auto leading-relaxed">
          Thank you, {form.full_name.split(" ")[0]}. Your transport enquiry is with our team now —
          a real person will review your route requirements and respond, usually within one working
          day. A confirmation email is on its way to {form.email}.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="transport-quote-form"
      className="bg-white border border-[#E2E8F0] rounded-[2rem] p-8 sm:p-10 shadow-[0_10px_30px_rgba(11,25,44,0.04)]"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="tq-name" className={labelCls}>Full name *</label>
          <input id="tq-name" data-testid="tq-input-name" required minLength={2}
            value={form.full_name} onChange={update("full_name")} placeholder="Jane Smith" className={inputCls} />
        </div>
        <div>
          <label htmlFor="tq-org" className={labelCls}>Organisation name *</label>
          <input id="tq-org" data-testid="tq-input-organisation" required minLength={2}
            value={form.organisation} onChange={update("organisation")} placeholder="Wigan Council / Oakfield School" className={inputCls} />
        </div>
        <div>
          <label htmlFor="tq-email" className={labelCls}>Email address *</label>
          <input id="tq-email" data-testid="tq-input-email" type="email" required
            value={form.email} onChange={update("email")} placeholder="j.smith@example.gov.uk" className={inputCls} />
        </div>
        <div>
          <label htmlFor="tq-phone" className={labelCls}>Contact telephone number *</label>
          <input id="tq-phone" data-testid="tq-input-phone" type="tel" required minLength={5}
            value={form.phone} onChange={update("phone")} placeholder="07123 456789" className={inputCls} />
        </div>
        <div>
          <label htmlFor="tq-type" className={labelCls}>Type of transport required *</label>
          <select id="tq-type" data-testid="tq-select-type" value={form.transport_type} onChange={update("transport_type")} className={inputCls}>
            {TRANSPORT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="tq-passengers" className={labelCls}>Number of passengers</label>
          <input id="tq-passengers" data-testid="tq-input-passengers" type="number" min="1"
            value={form.passengers} onChange={update("passengers")} placeholder="e.g. 8" className={inputCls} />
        </div>
        <div>
          <label htmlFor="tq-pickup" className={labelCls}>Pickup area</label>
          <input id="tq-pickup" data-testid="tq-input-pickup"
            value={form.pickup_area} onChange={update("pickup_area")} placeholder="e.g. Wigan" className={inputCls} />
        </div>
        <div>
          <label htmlFor="tq-destination" className={labelCls}>Destination area</label>
          <input id="tq-destination" data-testid="tq-input-destination"
            value={form.destination_area} onChange={update("destination_area")} placeholder="e.g. Bolton" className={inputCls} />
        </div>
        <div>
          <label htmlFor="tq-days" className={labelCls}>Days of operation</label>
          <input id="tq-days" data-testid="tq-input-days"
            value={form.days_of_operation} onChange={update("days_of_operation")} placeholder="e.g. Monday–Friday, term time" className={inputCls} />
        </div>
        <div>
          <label htmlFor="tq-start" className={labelCls}>Preferred start date</label>
          <input id="tq-start" data-testid="tq-input-startdate" type="date"
            value={form.start_date} onChange={update("start_date")} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="tq-duration" className={labelCls}>Estimated contract duration</label>
          <input id="tq-duration" data-testid="tq-input-duration"
            value={form.contract_duration} onChange={update("contract_duration")} placeholder="e.g. 12 months, academic year, ongoing" className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="tq-additional" className={labelCls}>Additional information</label>
          <textarea id="tq-additional" data-testid="tq-input-additional" rows={4}
            value={form.additional} onChange={update("additional")}
            placeholder="Routes, timings, vehicle or accessibility requirements..."
            className={`${inputCls} resize-none`} />
        </div>
        <div className="hidden" aria-hidden="true">
          <label htmlFor="tq-web">Website</label>
          <input id="tq-web" data-testid="tq-honeypot" tabIndex={-1} autoComplete="off"
            value={form.website} onChange={update("website")} />
        </div>
      </div>

      <div className="mt-6 flex items-start gap-3 bg-[#F1F5F9] rounded-2xl px-5 py-4">
        <ShieldCheck size={17} className="text-[#10B981] shrink-0 mt-0.5" />
        <p className="text-xs leading-relaxed text-[#475569]">
          Please do not include confidential medical information or children's personal details.
          Your business contact details are processed under UK GDPR solely to respond to your
          enquiry. All transport services are subject to operational capability, licensing,
          insurance, regulatory requirements and formal contract.
        </p>
      </div>

      <button
        type="submit"
        disabled={sending}
        data-testid="tq-submit-btn"
        className="group btn-shine mt-7 inline-flex items-center justify-center gap-2.5 w-full sm:w-auto bg-[#0B192C] text-white font-bold px-8 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {sending ? (
          <>Submitting <Loader2 size={16} className="animate-spin" /></>
        ) : (
          <>Submit Transport Enquiry <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></>
        )}
      </button>
    </form>
  );
};

export default Transport;
